import { FDS_SubscriptionTypes } from "constants/index";
import { Security, SecurityKind } from "helpers/types";

// Where a Security Master row's displayed price came from.
export type PriceSource = "fds" | "db";

export interface ResolvedPrice {
  value: number;
  source: PriceSource;
}

/**
 * The FDS subscription key for one page of rows, in the shape
 * useFDSWebSocket takes ("<TYPE>:<sym>,<sym>"), or null when the page is empty.
 *
 * FDS names a stock "AAPL-USA" and an option by its OCC symbol with each run
 * of padding spaces as "#" ("AAPL#250919C00190000-USA") — the same spelling
 * the Home page's strategy grid subscribes with.
 */
export const fdsSubKey = (
  kind: SecurityKind,
  rows: Pick<Security, "symbol">[],
): string | null => {
  const symbols = rows
    .map((row) => row.symbol)
    .filter((symbol): symbol is string => Boolean(symbol))
    .map((symbol) =>
      kind === "option"
        ? symbol.replace(/ +/g, "#") + "-USA"
        : symbol + "-USA",
    );

  if (symbols.length === 0) {
    return null;
  }

  const type =
    kind === "option"
      ? FDS_SubscriptionTypes.HOMEPAGE_OPTION
      : FDS_SubscriptionTypes.HOMEPAGE_STOCK;
  return type + ":" + symbols.join(",");
};

/**
 * The price to show: the FDS gateway's in-memory last price when it holds a
 * usable one for this symbol, otherwise the price stored on the asset.
 *
 * The gateway keys both stocks and options by the plain DB symbol, and reports
 * a field it has no value for as "N/A".
 */
export const resolvePrice = (
  row: Pick<Security, "symbol" | "last_price">,
  fdsCache: Record<string, any> | undefined,
): ResolvedPrice => {
  const cached = row.symbol ? fdsCache?.[row.symbol]?.last_price : undefined;
  // Only a non-empty value: Number("") is 0, which would pass as a price.
  const live =
    typeof cached === "string" && cached.trim() !== "" && cached !== "N/A"
      ? Number(cached)
      : NaN;

  if (Number.isFinite(live)) {
    return { value: live, source: "fds" };
  }
  return { value: Number(row.last_price), source: "db" };
};
